import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Container,
  Form,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import { dashboardService, User } from '../../services/dashboardService';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { getRoleDisplayLabel } from '../../utils/displayLabels';
import { getCurrentUserRole } from '../../utils/auth';
import { useWindowRefresh } from '../../hooks/useWindowRefresh';
import { sortByNewest } from '../../utils/sortByNewest';

interface MerchantReferences {
  merchantTransactionId?: string | null;
  merchantUserId?: string | null;
  merchantSessionId?: string | null;
  idempotencyKey?: string | null;
}

interface ReconciliationTransaction {
  id: number;
  userId: number;
  fiatAmount: number;
  fiatCurrency: string;
  cryptoCurrency?: string | null;
  receivedCryptoAmount?: number | null;
  distributedCryptoAmount?: number | null;
  reconciliationVariance?: number | null;
  needsReconciliationReview: boolean;
  status: string;
  displayStatus?: string;
  timestamp: string;
  txHash?: string | null;
  uniqueWalletAddress: string;
  publicPaymentId?: string | null;
  paymentSource?: string;
  merchantReferences?: MerchantReferences | null;
  isInvoice: boolean;
  invoice?: {
    invoiceNumber?: string | null;
  } | null;
}

interface ReconciliationFilters {
  reconciliationState: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  userId: string;
  search: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

const varianceThreshold = 0.000001;

const Reconciliation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = getCurrentUserRole();
  const canScopeUsers = userRole === 'Admin' || userRole === 'SuperAgent';

  const [transactions, setTransactions] = useState<ReconciliationTransaction[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ReconciliationFilters>({
    reconciliationState: 'all',
    status: '',
    dateFrom: '',
    dateTo: '',
    userId: '',
    search: '',
  });
  const [searchQueryDraft, setSearchQueryDraft] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 4,
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showUserDropdown, setShowUserDropdown] = useState<boolean>(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const usersData = await dashboardService.getUsers();
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (fetchError) {
      console.error('Failed to load reconciliation users', fetchError);
    }
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const buildQueryParams = useCallback((includePagination: boolean = true) => {
    const params = new URLSearchParams();

    if (filters.reconciliationState) {
      params.append('reconciliationState', filters.reconciliationState);
    }

    if (filters.status) {
      params.append('status', filters.status);
    }

    if (filters.dateFrom) {
      params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    }

    if (filters.dateTo) {
      params.append('dateTo', new Date(filters.dateTo).toISOString());
    }

    if (filters.userId) {
      params.append('userId', filters.userId);
    }

    if (filters.search.trim()) {
      params.append('search', filters.search.trim());
    }

    if (includePagination) {
      params.append('page', pagination.currentPage.toString());
      params.append('pageSize', pagination.itemsPerPage.toString());
    }

    return params;
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  const fetchTransactions = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}?${buildQueryParams().toString()}`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const items = sortByNewest(
        Array.isArray(data?.items) ? (data.items as ReconciliationTransaction[]) : [],
        {
          dateKeys: ['timestamp'],
          numericFallbackKeys: ['id'],
        },
      );
      setTransactions(items);

      const count = typeof data?.count === 'number' ? data.count : items.length;
      const page = typeof data?.page === 'number' ? data.page : pagination.currentPage;
      const pageSize = typeof data?.pageSize === 'number' ? data.pageSize : pagination.itemsPerPage;

      setPagination((previous) => ({
        ...previous,
        currentPage: page,
        totalPages: Math.max(1, Math.ceil(count / pageSize)),
        totalItems: count,
        itemsPerPage: pageSize,
      }));
    } catch (fetchError) {
      console.error('Failed to load reconciliation rows', fetchError);
      if (!silent) {
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to load reconciliation rows');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [buildQueryParams, getAuthHeaders, pagination.currentPage, pagination.itemsPerPage]);

  const downloadCsv = useCallback(async () => {
    try {
      setError(null);

      const response = await fetch(`${API_ENDPOINTS.TRANSACTIONS}/export/csv?${buildQueryParams(false).toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `reconciliation-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Failed to export reconciliation CSV', downloadError);
      setError(downloadError instanceof Error ? downloadError.message : 'Failed to export reconciliation CSV');
    }
  }, [buildQueryParams, getAuthHeaders]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    void fetchTransactions();
  }, [fetchTransactions]);

  useWindowRefresh(() => {
    void fetchTransactions(true);
  }, {
    intervalMs: 30000,
  });

  useEffect(() => {
    setSearchQueryDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const visibleUsers = useMemo(
    () => [...users].sort((left, right) => left.name.localeCompare(right.name)),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return visibleUsers.filter((user) => {
      if (!normalizedSearchTerm) {
        return true;
      }

      const haystack = [
        user.name,
        user.email,
        user.settlementWallet,
        getRoleDisplayLabel(user.role),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearchTerm);
    });
  }, [searchTerm, visibleUsers]);

  const selectedUser = useMemo(
    () => visibleUsers.find((user) => user.id.toString() === filters.userId) ?? null,
    [filters.userId, visibleUsers]
  );

  const summary = useMemo(() => {
    const needsReview = transactions.filter((transaction) => transaction.needsReconciliationReview).length;
    const missingData = transactions.filter((transaction) =>
      transaction.receivedCryptoAmount == null || transaction.distributedCryptoAmount == null
    ).length;
    const matched = transactions.filter((transaction) =>
      !transaction.needsReconciliationReview &&
      transaction.receivedCryptoAmount != null &&
      transaction.distributedCryptoAmount != null
    ).length;
    const varianceWatch = transactions.filter((transaction) =>
      transaction.reconciliationVariance != null &&
      Math.abs(transaction.reconciliationVariance) >= varianceThreshold
    ).length;

    return {
      visible: transactions.length,
      needsReview,
      missingData,
      matched,
      varianceWatch,
    };
  }, [transactions]);

  const activeFilterCount = useMemo(
    () => [
      filters.reconciliationState !== 'all' ? filters.reconciliationState : '',
      filters.status,
      filters.dateFrom,
      filters.dateTo,
      filters.userId,
      filters.search.trim(),
    ].filter(Boolean).length,
    [filters.dateFrom, filters.dateTo, filters.reconciliationState, filters.search, filters.status, filters.userId]
  );

  const reviewStateLabel = useMemo(() => {
    switch (filters.reconciliationState) {
      case 'needs_review':
        return 'Needs review';
      case 'matched':
        return 'Matched';
      case 'missing_data':
        return 'Missing data';
      default:
        return 'All payout rows';
    }
  }, [filters.reconciliationState]);

  const summaryCardStyle = (accent: string) =>
    ({ ['--summary-accent' as '--summary-accent']: accent } as React.CSSProperties);

  const formatAmount = (amount?: number | null) => {
    if (amount == null || Number.isNaN(amount)) {
      return '-';
    }

    return amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  const formatDateTime = (value: string) => new Date(value).toLocaleString();

  const truncateAddress = (value?: string | null) => {
    if (!value) {
      return '-';
    }

    return value.length <= 14 ? value : `${value.slice(0, 8)}...${value.slice(-6)}`;
  };

  const getReferenceLabel = (transaction: ReconciliationTransaction) =>
    transaction.merchantReferences?.merchantTransactionId ||
    transaction.merchantReferences?.merchantUserId ||
    transaction.invoice?.invoiceNumber ||
    transaction.publicPaymentId ||
    `Transaction #${transaction.id}`;

  const getReviewBadge = (transaction: ReconciliationTransaction) => {
    if (transaction.receivedCryptoAmount == null || transaction.distributedCryptoAmount == null) {
      return <span className="app-status-pill app-status-pill--warning">Missing data</span>;
    }

    if (transaction.needsReconciliationReview) {
      return <span className="app-status-pill app-status-pill--danger">Needs review</span>;
    }

    return <span className="app-status-pill app-status-pill--success">Matched</span>;
  };

  const handleFilterChange = (key: keyof ReconciliationFilters, value: string) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  };

  const handleUserSelect = (selectedUserId: string) => {
    setFilters((previous) => ({
      ...previous,
      userId: selectedUserId,
    }));
    setSearchTerm('');
    setShowUserDropdown(false);
    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  };

  const applySearchFilter = () => {
    const normalizedSearch = searchQueryDraft.trim();

    setFilters((previous) => {
      if (previous.search === normalizedSearch) {
        return previous;
      }

      return {
        ...previous,
        search: normalizedSearch,
      };
    });

    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      reconciliationState: 'all',
      status: '',
      dateFrom: '',
      dateTo: '',
      userId: '',
      search: '',
    });
    setSearchTerm('');
    setSearchQueryDraft('');
    setShowUserDropdown(false);
    setPagination((previous) => ({
      ...previous,
      currentPage: 1,
    }));
  };

  const handleOpenTransaction = (transactionId: number) => {
    const returnTo = `${location.pathname}${location.search}`;
    const params = new URLSearchParams();
    params.set('search', transactionId.toString());
    params.set('openTransactionId', transactionId.toString());
    params.set('returnTo', returnTo);
    navigate({
      pathname: '/transactions',
      search: `?${params.toString()}`,
    });
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) {
      return null;
    }

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let page = startPage; page <= endPage; page += 1) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === pagination.currentPage}
          onClick={() => setPagination((previous) => ({ ...previous, currentPage: page }))}
        >
          {page}
        </Pagination.Item>,
      );
    }

    return (
      <Pagination className="app-pagination app-pagination--operator justify-content-center flex-wrap">
        <Pagination.Prev
          disabled={pagination.currentPage === 1}
          onClick={() => pagination.currentPage > 1 && setPagination((previous) => ({
            ...previous,
            currentPage: previous.currentPage - 1,
          }))}
        />
        {items}
        <Pagination.Next
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() => pagination.currentPage < pagination.totalPages && setPagination((previous) => ({
            ...previous,
            currentPage: previous.currentPage + 1,
          }))}
        />
      </Pagination>
    );
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="operator-page reconciliation-page">
        <Container fluid className="operator-page__container">
          <Card className="operator-card reconciliation-page__loading-card">
            <Card.Body className="text-center">
              <Spinner animation="border" role="status" className="app-spinner">
                <span className="visually-hidden">Loading reconciliation rows...</span>
              </Spinner>
              <p className="operator-muted-text mb-0 mt-3">
                Loading the latest payout review rows...
              </p>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="operator-page reconciliation-page">
      <Container fluid className="operator-page__container">
        <InternalPageHeader
          eyebrow="Finance Review"
          title="Reconciliation"
          description="Compare received funds and payout totals so mismatches stand out fast."
          actions={(
            <div className="app-toolbar-group">
              <Button variant="outline-light" onClick={() => void fetchTransactions()} disabled={loading}>
                {loading ? <Spinner size="sm" /> : 'Refresh'}
              </Button>
              <Button variant="primary" onClick={() => void downloadCsv()}>
                Export CSV
              </Button>
            </div>
          )}
          meta={[
            { label: 'Rows in view', value: summary.visible.toString() },
            { label: 'Needs review', value: summary.needsReview.toString() },
            { label: 'Missing data', value: summary.missingData.toString() },
            { label: 'Scope', value: selectedUser ? selectedUser.name : getRoleDisplayLabel(userRole) },
          ]}
        />

        <div className="operator-summary-grid mt-4">
          <div className="operator-summary-card" style={summaryCardStyle('var(--app-blue)')}>
            <div className="operator-summary-card__label">Visible rows</div>
            <div className="operator-summary-card__value">{summary.visible}</div>
            <div className="operator-summary-card__note">
              Payout rows in the current finance review view.
            </div>
          </div>
          <div className="operator-summary-card" style={summaryCardStyle('var(--app-gray-300)')}>
            <div className="operator-summary-card__label">Needs review</div>
            <div className="operator-summary-card__value">{summary.needsReview}</div>
            <div className="operator-summary-card__note">
              Rows where received and distributed values still need a closer look.
            </div>
          </div>
          <div className="operator-summary-card" style={summaryCardStyle('var(--app-gray-500)')}>
            <div className="operator-summary-card__label">Missing data</div>
            <div className="operator-summary-card__value">{summary.missingData}</div>
            <div className="operator-summary-card__note">
              Rows still waiting for one or more on-chain values to come through.
            </div>
          </div>
          <div className="operator-summary-card" style={summaryCardStyle('var(--app-text-muted)')}>
            <div className="operator-summary-card__label">Variance watch</div>
            <div className="operator-summary-card__value">{summary.varianceWatch}</div>
            <div className="operator-summary-card__note">
              Rows with a visible variance that deserves a second check.
            </div>
          </div>
        </div>

        <Card className="operator-card mt-4 reconciliation-page__filters-card">
          <Card.Header className="operator-card__header">
            <div>
              <h5 className="operator-card__title">Review controls</h5>
              <div className="operator-card__subtitle">
                Narrow the review list by state, account, date, or payment reference.
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="reconciliation-page__filters-layout">
              <div className="reconciliation-page__quick-filters">
                {[
                  { label: 'All rows', value: 'all' },
                  { label: 'Needs review', value: 'needs_review' },
                  { label: 'Matched', value: 'matched' },
                  { label: 'Missing data', value: 'missing_data' },
                ].map((option) => {
                  const isActive = filters.reconciliationState === option.value;

                  return (
                    <Button
                      key={option.value}
                      variant={isActive ? 'primary' : 'outline-light'}
                      className="reconciliation-page__quick-filter"
                      onClick={() => handleFilterChange('reconciliationState', option.value)}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </div>

              <div className="reconciliation-page__filter-grid">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    className="operator-control"
                    value={filters.status}
                    onChange={(event) => handleFilterChange('status', event.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="Issued">Issued</option>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="PayoutCompleted">Payout completed</option>
                    <option value="Failed">Failed</option>
                    <option value="Expired">Expired</option>
                  </Form.Select>
                </Form.Group>

                {canScopeUsers && (
                  <Form.Group>
                    <Form.Label>Account</Form.Label>
                    <div className={`operator-search-surface ${showUserDropdown ? 'operator-search-surface--open' : ''}`} ref={userDropdownRef}>
                      <Form.Control
                        className="operator-control"
                        type="text"
                        placeholder={selectedUser ? selectedUser.name : 'Search merchant or sub merchant'}
                        value={searchTerm}
                        onChange={(event) => {
                          setSearchTerm(event.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                      />
                      <button
                        type="button"
                        className="operator-search-toggle"
                        onClick={() => setShowUserDropdown((previous) => !previous)}
                        aria-label="Toggle account filter"
                      >
                        <span className="operator-toggle-indicator">
                          {showUserDropdown ? '-' : '+'}
                        </span>
                      </button>

                      {showUserDropdown && (
                        <div className="operator-search-menu">
                          <button
                            type="button"
                            className={`operator-search-option ${filters.userId === '' ? 'operator-search-option--active' : ''}`}
                            onClick={() => handleUserSelect('')}
                          >
                            All accounts
                            <span className="operator-search-option__meta">
                              Show every payout row visible to your current role.
                            </span>
                          </button>

                          {filteredUsers.length === 0 ? (
                            <div className="reconciliation-page__search-empty">
                              No accounts match this search yet.
                            </div>
                          ) : (
                            filteredUsers.map((user) => (
                              <button
                                type="button"
                                key={user.id}
                                className={`operator-search-option ${filters.userId === user.id.toString() ? 'operator-search-option--active' : ''}`}
                                onClick={() => handleUserSelect(user.id.toString())}
                              >
                                {user.name}
                                <span className="operator-search-option__meta">
                                  {getRoleDisplayLabel(user.role)} / {user.email}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                )}

                <Form.Group className={canScopeUsers ? '' : 'reconciliation-page__search-wide'}>
                  <Form.Label>Search</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control
                      className="operator-control"
                      type="text"
                      value={searchQueryDraft}
                      onChange={(event) => setSearchQueryDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          applySearchFilter();
                        }
                      }}
                      placeholder="Transaction, invoice, player, payment ID, or tx hash"
                    />
                    <Button
                      variant="outline-light"
                      onClick={applySearchFilter}
                    >
                      Apply
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group>
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    className="operator-control"
                    type="datetime-local"
                    value={filters.dateFrom}
                    onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Until</Form.Label>
                  <Form.Control
                    className="operator-control"
                    type="datetime-local"
                    value={filters.dateTo}
                    onChange={(event) => handleFilterChange('dateTo', event.target.value)}
                  />
                </Form.Group>

                <div className="reconciliation-page__filter-help">
                  <span className="reconciliation-page__filter-help-title">Current review view</span>
                  <span className="reconciliation-page__filter-help-copy">
                    {reviewStateLabel}. {selectedUser
                      ? `Scoped to ${selectedUser.name}.`
                      : `Showing every row visible to ${getRoleDisplayLabel(userRole)}.`}{' '}
                    Export from the header when finance needs the same view in CSV form.
                  </span>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="reconciliation-page__active-filters">
                  <span className="reconciliation-page__active-filters-label">
                    {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
                  </span>
                  <Button variant="outline-light" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {loading && transactions.length > 0 && (
          <div className="reconciliation-page__refresh-banner mt-4">
            Refreshing the review queue with the latest payout data...
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mb-4 mt-4">
            {error}
          </Alert>
        )}

        <Card className="operator-card mt-4 reconciliation-page__queue-card">
          <Card.Header className="operator-card__header d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h5 className="operator-card__title">Review queue</h5>
              <div className="operator-card__subtitle">
                Open the transaction only when you need the full case history. Routine payout checks can stay right here.
              </div>
            </div>
            <span className="operator-muted-text">{pagination.totalItems} total rows</span>
          </Card.Header>
          <Card.Body>
            {!loading && transactions.length === 0 ? (
              <div className="app-empty-state reconciliation-page__empty-state">
                No reconciliation rows matched the current review filters.
              </div>
            ) : (
              <div className="reconciliation-page__list">
                {transactions.map((transaction) => {
                  const variance = transaction.reconciliationVariance;
                  const reviewStateClass =
                    transaction.receivedCryptoAmount == null || transaction.distributedCryptoAmount == null
                      ? 'reconciliation-page__item--missing'
                      : transaction.needsReconciliationReview
                        ? 'reconciliation-page__item--attention'
                        : 'reconciliation-page__item--matched';

                  return (
                    <article key={transaction.id} className={`reconciliation-page__item ${reviewStateClass}`}>
                      <div className="reconciliation-page__item-top">
                        <div className="reconciliation-page__item-main">
                          <div className="reconciliation-page__item-eyebrow">
                            {transaction.isInvoice ? 'Invoice review' : 'Payment link review'}
                          </div>
                          <h6 className="reconciliation-page__item-title">
                            {getReferenceLabel(transaction)}
                          </h6>
                          <div className="reconciliation-page__item-subtitle">
                            Transaction #{transaction.id} / {formatDateTime(transaction.timestamp)}
                          </div>
                        </div>

                        <div className="reconciliation-page__item-statuses">
                          {getReviewBadge(transaction)}
                          <span className="app-status-pill app-status-pill--neutral">
                            {transaction.displayStatus || transaction.status}
                          </span>
                        </div>
                      </div>

                      <div className="reconciliation-page__metrics">
                        <div className="reconciliation-page__metric">
                          <span className="reconciliation-page__metric-label">Received</span>
                          <strong>{formatAmount(transaction.receivedCryptoAmount)} {transaction.cryptoCurrency || 'USDC'}</strong>
                        </div>
                        <div className="reconciliation-page__metric">
                          <span className="reconciliation-page__metric-label">Distributed</span>
                          <strong>{formatAmount(transaction.distributedCryptoAmount)} {transaction.cryptoCurrency || 'USDC'}</strong>
                        </div>
                        <div className="reconciliation-page__metric">
                          <span className="reconciliation-page__metric-label">Variance</span>
                          <strong className={variance != null && Math.abs(variance) >= varianceThreshold ? 'app-variance--alert' : ''}>
                            {variance == null
                              ? '-'
                              : `${variance > 0 ? '+' : ''}${formatAmount(variance)} ${transaction.cryptoCurrency || 'USDC'}`}
                          </strong>
                        </div>
                        <div className="reconciliation-page__metric">
                          <span className="reconciliation-page__metric-label">Commercial amount</span>
                          <strong>{transaction.fiatAmount} {transaction.fiatCurrency}</strong>
                        </div>
                      </div>

                      <div className="reconciliation-page__meta-grid">
                        <div className="reconciliation-page__meta-item">
                          <span className="reconciliation-page__meta-label">Payment source</span>
                          <strong>{transaction.paymentSource || 'Direct crypto'}</strong>
                        </div>
                        <div className="reconciliation-page__meta-item">
                          <span className="reconciliation-page__meta-label">Payout address</span>
                          <strong title={transaction.uniqueWalletAddress}>{truncateAddress(transaction.uniqueWalletAddress)}</strong>
                        </div>
                        <div className="reconciliation-page__meta-item">
                          <span className="reconciliation-page__meta-label">Chain record</span>
                          {transaction.txHash ? (
                            <a
                              href={`https://polygonscan.com/tx/${transaction.txHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="app-table-link"
                            >
                              Open chain record
                            </a>
                          ) : (
                            <span className="operator-muted-text">Pending</span>
                          )}
                        </div>
                      </div>

                      <div className="reconciliation-page__item-actions">
                        <Button variant="outline-light" onClick={() => handleOpenTransaction(transaction.id)}>
                          Open transaction
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-4">
          <div className="app-section-footer">
            Received, distributed, and variance stay aligned with the CSV export for finance handoff.
          </div>
          {renderPagination()}
        </div>
      </Container>
    </div>
  );
};

export default Reconciliation;
